import { setUpgradeModal } from '../actions/App';

import { getStore } from './App';
import { userHasFeature } from './Features';

export const getUpgradePlanText = (feature, intl) => {
  const shareTexts = {
    // No id: here, it's unified with shareFeatures array
    header: intl.formatMessage({ defaultMessage: 'Share your files, your way' }),
    subheader: intl.formatMessage({ defaultMessage: 'Add passwords, watermarks, expirations, and more.' }),
    others: [
      intl.formatMessage({ defaultMessage: 'Create share links with view-only files and Collections ' }),
      intl.formatMessage({ defaultMessage: 'Limit the available file sizes' }),
      intl.formatMessage({ defaultMessage: 'Require an email or password to download' }),
      intl.formatMessage({ defaultMessage: 'Allow external user comments and favoriting' }),
      intl.formatMessage({ defaultMessage: 'Add expiration dates and watermarks' }),
      intl.formatMessage({ defaultMessage: 'Allow file status changes' }),
    ],
  };

  const shareFeatures = [
    'share-link-settings',
    'share-watermark',
    'share-file-sizes',
    'share-require-name',
    'share-require-email',
    'share-allow-favoriting',
    'share-allow-comments',
    'share-status-change',
    'share-custom-branding',
    'share-require-password',
    'share-require-login',
    'share-require-login',
    'share-expiration-date',
    'share-allow-download',
    'share-download-reasons',
    'share-hide-file-details',
  ];

  const texts = [
    // Share
    ...shareFeatures.map(id => ({
      id,
      modal: true,
      video: 'shares',
      image: '/images/empty-pages/empty-share-links-page.png',
      ...shareTexts,
    })),
    {
      id: 'share-templates',
      modal: true,
      video: 'share-templates',
      learnMore: 'https://help.stockpress.co/en/articles/9810537-creating-share-templates',
      header: intl.formatMessage({ defaultMessage: 'Create reusable share templates' }),
      subheader: intl.formatMessage({ defaultMessage: 'Skip enabling advanced share settings again and again with every link. Enable share templates to create reusable settings for personal use or create templates available for your entire team.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Make reusable templates with your favorite settings' }),
        intl.formatMessage({ defaultMessage: 'Add custom branding and colors' }),
      ],
    },
    // End Share

    {
      id: 'duplicate-files',
      modal: true,
      video: 'duplicates',
      learnMore: 'https://help.stockpress.co/en/articles/6782508-identifying-duplicate-files',
      header: intl.formatMessage({ defaultMessage: 'Automatically detect duplicate files' }),
      subheader: intl.formatMessage({ defaultMessage: 'Find and merge duplicate files to save storage space and confusion over which file is the right one.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Automatically find duplicate images, PDFs, videos, design files, and more!' }),
        intl.formatMessage({ defaultMessage: 'Save on storage space' }),
        intl.formatMessage({ defaultMessage: 'Merge duplicates and retain metadata' }),
        intl.formatMessage({ defaultMessage: 'Identify similar files' }),
      ],
    },
    {
      id: 'file-versions',
      modal: true,
      video: 'file-versions',
      learnMore: 'https://help.stockpress.co/en/articles/5937566-adding-new-file-versions',
      header: intl.formatMessage({ defaultMessage: 'Upload new File Versions' }),
      subheader: intl.formatMessage({ defaultMessage: 'Keep every version at your fingertips and eliminate confusion over which file is the right one.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Access the most up-to-date files' }),
        intl.formatMessage({ defaultMessage: 'Keep track of every version' }),
        intl.formatMessage({ defaultMessage: 'Restore previous versions' }),
      ],
    },
    {
      id: 'image-editor',
      modal: true,
      video: 'image-editor',
      learnMore: 'https://help.stockpress.co/en/articles/9341551-crop-assets-with-our-image-editor',
      header: intl.formatMessage({ defaultMessage: 'Image editing, built right in' }),
      subheader: intl.formatMessage({ defaultMessage: 'Crop, rotate, flip, and remove backgrounds from images—without switching tools or leaving your workspace.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Crop, resize, rotate, or flip' }),
        intl.formatMessage({ defaultMessage: 'Download pre-set social sizes' }),
        intl.formatMessage({ defaultMessage: 'Remove backgrounds' }),
        intl.formatMessage({ defaultMessage: 'Save new file Editions' }),
        intl.formatMessage({ defaultMessage: 'Save new file Versions' }),
      ],
    },
    {
      id: 'file-annotations',
      modal: true,
      video: 'file-annotations',
      learnMore: 'https://help.stockpress.co/en/articles/5937476-leaving-comments-and-annotations-on-images-videos-and-pdfs',
      header: intl.formatMessage({ defaultMessage: 'Leave annotated comments on files' }),
      subheader: intl.formatMessage({ defaultMessage: 'Leave pinpointed feedback right on images, PDFs and videos to collaborate visually without endless email threads.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Click and drag to comment' }),
        intl.formatMessage({ defaultMessage: 'Jump to annotations' }),
        intl.formatMessage({ defaultMessage: 'Leave time-based feedback on video' }),
      ],
    },
    {
      id: 'file-sizes',
      modal: true,
      video: 'file-sizes',
      learnMore: 'https://help.stockpress.co/en/articles/5937873-add-multiple-file-sizes-for-downloads',
      header: intl.formatMessage({ defaultMessage: 'Create Custom Download Sizes' }),
      subheader: intl.formatMessage({ defaultMessage: 'Get perfectly sized files in seconds — no extra resizing tools needed.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Enable multiple file download sizes' }),
        intl.formatMessage({ defaultMessage: 'Automatically optimize images' }),
      ],
    },
    {
      id: 'file-status',
      modal: true,
      video: 'file-status',
      learnMore: 'https://help.stockpress.co/en/articles/5937871-creating-file-statuses',
      header: intl.formatMessage({ defaultMessage: 'Create File Statuses' }),
      subheader: intl.formatMessage({ defaultMessage: 'See file progress at a glance and easily searchable to keep projects moving smoothly.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Create light-touch workflows' }),
        intl.formatMessage({ defaultMessage: 'Filter to specific statuses' }),
      ],
    },
    {
      id: 'custom-file-thumbnail',
      others: [],
    },
    {
      id: 'custom-pages',
      modal: true,
      video: 'custom-pages',
      learnMore: 'https://help.stockpress.co/en/articles/7032397-creating-custom-pages',
      header: intl.formatMessage({ defaultMessage: 'Turn your workspace content into beautiful, branded experiences' }),
      subheader: intl.formatMessage({ defaultMessage: 'Forget sending plain links! Content Portals lets you create shareable, professional-looking web pages directly in Stockpress using your workspace content and an easy-to-use drag-and-drop builder.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Add Collections, file feeds and video' }),
        intl.formatMessage({ defaultMessage: 'Create brand guides and custom share portals' }),
        intl.formatMessage({ defaultMessage: 'Add custom colors, fonts and logos' }),
      ],
    },
    {
      id: 'custom-teams',
      modal: true,
      video: 'teams',
      learnMore: 'https://help.stockpress.co/en/articles/5937385-creating-teams-in-your-workspace',
      header: intl.formatMessage({ defaultMessage: 'Organize file access with Teams' }),
      subheader: intl.formatMessage({ defaultMessage: 'Groups your workspace users to give access to files and Collections in just a few clicks.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Organize your workspace users' }),
        intl.formatMessage({ defaultMessage: 'Quickly give access to multiple users at once' }),
      ],
    },
    {
      id: 'custom-roles',
      modal: true,
      video: 'custom-roles',
      learnMore: 'https://help.stockpress.co/en/articles/5937840-creating-custom-roles',
      header: intl.formatMessage({ defaultMessage: 'Custom user permissions made just for you' }),
      subheader: intl.formatMessage({ defaultMessage: 'Create custom roles that work for your team. Decide who can download, upload, share, edit, and more!' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Create unlimited combinations of capabilities' }),
        intl.formatMessage({ defaultMessage: 'Limit uploading, downloading, or sharing' }),
      ],
    },
    {
      id: 'reports',
      modal: true,
      video: 'reports',
      learnMore: 'https://help.stockpress.co/en/articles/9405140-usage-reports',
      header: intl.formatMessage({ defaultMessage: 'Get workspace insights with real-time data' }),
      subheader: intl.formatMessage({ defaultMessage: 'View and generate reports on workspace usage, search terms, popular files, most active users and teams, and more!' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Track file usage and engagement' }),
        intl.formatMessage({ defaultMessage: 'See user and team history' }),
      ],
    },
    {
      id: 'branding',
      modal: true,
      video: 'branding',
      header: intl.formatMessage({ defaultMessage: 'Brand your workspace' }),
      subheader: intl.formatMessage({ defaultMessage: 'Make your workspace feel like home with your own logo and colors.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Add a custom workspace logo' }),
        intl.formatMessage({ defaultMessage: 'Add a custom share page logo' }),
        intl.formatMessage({ defaultMessage: 'Add your brand color' }),
      ],
    },
    {
      id: 'metadata-mapping',
      modal: true,
      learnMore: 'https://help.stockpress.co/en/articles/8381132-metadata-mapping',
      header: intl.formatMessage({ defaultMessage: 'Metadata Mapping' }),
      subheader: intl.formatMessage({ defaultMessage: 'Sync file info automatically and skip repetitive manual tagging.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Automatically add file metadata during upload' }),
        intl.formatMessage({ defaultMessage: 'Sync unlimited data to custom fields' }),
        intl.formatMessage({ defaultMessage: 'Reduce or eliminate manual tagging' }),
      ],
    },
    {
      id: 'download-file-with-metadata',
      modal: true,
      image: '/images/empty-pages/empty-metadata-mapping.png',
      header: intl.formatMessage({ defaultMessage: 'Apply workspace metadata on download' }),
      subheader: intl.formatMessage({ defaultMessage: 'Sync metadata added to your files in your workspace to EXIF, IPTC, and/or XMP fields and never lose a tag or description.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Create custom mapping to metadata fields' }),
        intl.formatMessage({ defaultMessage: 'Update file metadata to match your workspace' }),
      ],
    },
    {
      id: 'export-metadata',
      modal: true,
      header: intl.formatMessage({ defaultMessage: 'Export Metadata' }),
      subheader: intl.formatMessage({ defaultMessage: 'Export all your file data to a CSV.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Audit categories and tags' }),
        intl.formatMessage({ defaultMessage: 'Fill in missing metadata' }),
      ],
    },
    {
      id: 'watermarking',
      modal: true,
      video: 'watermarking',
      learnMore: 'https://help.stockpress.co/en/articles/6909564-adding-custom-branded-watermarks-to-files-and-or-share-links',
      header: intl.formatMessage({ defaultMessage: 'Watermark your files' }),
      subheader: intl.formatMessage({ defaultMessage: 'Protect your brand with automatic and manual watermarking on files in your workspace and in share links.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Add your own logo' }),
        intl.formatMessage({ defaultMessage: 'Select a position' }),
        intl.formatMessage({ defaultMessage: 'Watermark workspace images and share links' }),
      ],
    },
    {
      id: 'public-file-urls',
      modal: true,
      video: 'public-file-urls',
      learnMore: 'https://help.stockpress.co/en/articles/9592035-creating-public-urls',
      header: intl.formatMessage({ defaultMessage: 'Public URLs' }),
      subheader: intl.formatMessage({ defaultMessage: 'Create a publicly visible version of a file to share or embed on external websites—no login or shared link is required.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Create public file links' }),
        intl.formatMessage({ defaultMessage: 'Embed files on external sites' }),
        intl.formatMessage({ defaultMessage: 'Keep links updated with versions' }),
      ],
    },
    {
      id: 'ftp',
      modal: true,
      image: '/images/empty-pages/empty-ftp.png',
      learnMore: 'https://help.stockpress.co/en/articles/7188548-ftp-importer',
      header: intl.formatMessage({ defaultMessage: 'File FTP' }),
      subheader: intl.formatMessage({ defaultMessage: 'Transfer high volumes of files quickly and securely — straight into Stockpress from any connected device.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Create unlimited FTP connections' }),
        intl.formatMessage({ defaultMessage: 'Receive files and folders in workspace Collections' }),
      ],
    },
    {
      id: 'ftp-queue',
      modal: true,
      image: '/images/empty-pages/empty-ftp.png',
      learnMore: 'https://help.stockpress.co/en/articles/7188548-ftp-importer',
      header: intl.formatMessage({ defaultMessage: 'File FTP' }),
      subheader: intl.formatMessage({ defaultMessage: 'Transfer high volumes of files quickly and securely — straight into Stockpress from any connected device.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Create unlimited FTP connections' }),
        intl.formatMessage({ defaultMessage: 'Receive files and folders in workspace Collections' }),
      ],
    },
    {
      id: 'upload-pages',
      modal: true,
      video: 'upload-pages',
      learnMore: 'https://help.stockpress.co/en/articles/8902917-external-upload-pages',
      header: intl.formatMessage({ defaultMessage: 'Get files from anyone — no login required' }),
      subheader: intl.formatMessage({ defaultMessage: 'Create public pages for external users to upload files directly to Collections in your workspace.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Receive files from anyone' }),
        intl.formatMessage({ defaultMessage: 'Customize link permissions' }),
        intl.formatMessage({ defaultMessage: 'Set passwords and expiration dates' }),
      ],
    },
    {
      id: 'archive',
      modal: true,
      video: 'archive',
      learnMore: 'https://help.stockpress.co/en/articles/5937489-archiving-files-and-collections',
      header: intl.formatMessage({ defaultMessage: 'Out of sight, but still in reach' }),
      subheader: intl.formatMessage({ defaultMessage: 'Move outdated files to the Archive and access them only when you need them - freeing up your workspace for active projects.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Keep your workspace current' }),
        intl.formatMessage({ defaultMessage: 'Limit access to depreciated files' }),
        intl.formatMessage({ defaultMessage: 'Allow access to custom user roles' }),
      ],
    },
    {
      id: 'custom-categories',
      modal: true,
      video: 'tag-categories',
      learnMore: 'https://help.stockpress.co/en/articles/5937851-creating-and-managing-custom-categories',
      header: intl.formatMessage({ defaultMessage: 'Categorize your tagging' }),
      subheader: intl.formatMessage({ defaultMessage: 'Organize files your way with custom tag categories that match your workflow.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Customize your tag groups' }),
        intl.formatMessage({ defaultMessage: 'Set tagging permissions' }),
      ],
    },
    {
      id: 'custom-fields',
      modal: true,
      video: 'custom-fields',
      header: intl.formatMessage({ defaultMessage: 'Your files, your fields' }),
      subheader: intl.formatMessage({ defaultMessage: 'Create custom fields to store extra details about your files—from notes and credits to links and references. Organize metadata beyond tags and make your content more searchable and meaningful.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Add free-text or link-based custom fields' }),
        intl.formatMessage({ defaultMessage: 'Include notes, credits, or external references' }),
        intl.formatMessage({ defaultMessage: 'Keep important details connected to your assets' }),
        intl.formatMessage({ defaultMessage: 'Set custom field permissions' }),
      ],
    },
    {
      id: 'expiration-date',
      modal: true,
      video: 'expiration-date',
      learnMore: 'https://help.stockpress.co/en/articles/6909745-adding-expiration-dates-to-files',
      header: intl.formatMessage({ defaultMessage: 'Put file usage on a timer' }),
      subheader: intl.formatMessage({ defaultMessage: 'Add expiration dates to files so you stay in control of what’s used or shared and for how long.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Set future or backdated expiration dates' }),
        intl.formatMessage({ defaultMessage: 'Automatically watermark expired files' }),
        intl.formatMessage({ defaultMessage: 'Make expired files non-downloadable' }),
        intl.formatMessage({ defaultMessage: 'Filter to expiring or expired content' }),
      ],
    },
    {
      id: 'adobe-photoshop',
      modal: true,
      learnMore: 'https://help.stockpress.co/en/articles/10206850-how-to-integrate-stockpress-with-adobe-photoshop',
      header: intl.formatMessage({ defaultMessage: 'Connect with Adobe Photoshop' }),
      subheader: intl.formatMessage({ defaultMessage: 'Find and add your files from Stockpress to your Adobe Photoshop projects and sync them back to Stockpress.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Open, edit, and place files' }),
        intl.formatMessage({ defaultMessage: 'Save, version, and export' }),
      ],
    },
    {
      id: 'user-invite-note',
      others: [],
    },
    {
      id: 'face-recognition',
      modal: true,
      video: 'face-recognition',
      learnMore: 'https://help.stockpress.co/en/articles/12415334-facial-recognition',
      header: intl.formatMessage({ defaultMessage: 'Find your people, fast — with facial recognition' }),
      subheader: intl.formatMessage({ defaultMessage: 'Automatically identify and tag the people in your photos, then let AI find them everywhere else. From team headshots to event photos, searching and filtering by people just got easier.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Identify and name people' }),
        intl.formatMessage({ defaultMessage: 'View all images associated with a named person' }),
        intl.formatMessage({ defaultMessage: 'Merge people' }),
        intl.formatMessage({ defaultMessage: 'Hide faces' }),
      ],
    },
    {
      id: 'file-name-prefix-suffix',
      modal: true,
      video: 'file-name-prefix-suffix',
      learnMore: 'https://help.stockpress.co/en/articles/11567055-file-name-prefix-and-suffix-templates',
      header: intl.formatMessage({ defaultMessage: 'Name your files smarter, not harder' }),
      subheader: intl.formatMessage({ defaultMessage: 'Add prefixes or suffixes to your files during upload to keep everything neatly organized — automatically.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Add prefixes and/or suffixes during upload' }),
        intl.formatMessage({ defaultMessage: 'Customize separators (dash, underscore, and more)' }),
        intl.formatMessage({ defaultMessage: 'Choose what number or letter to start with' }),
        intl.formatMessage({ defaultMessage: 'Choose uppercase or lowercase,' }),
        intl.formatMessage({ defaultMessage: 'Rename files in bulk for easy organization' }),
      ],
    },
    {
      id: 'metadata-groups',
      modal: true,
      video: 'metadata-groups',
      learnMore: 'https://help.stockpress.co/en/articles/14294780-metadata-groups',
      header: intl.formatMessage({ defaultMessage: 'Show only the metadata that matters — file by file' }),
      subheader: intl.formatMessage({ defaultMessage: 'Bundle your Tag Categories and Custom Fields into focused groups so each file displays just the fields relevant to it.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Group related Tag Categories and Custom Fields together' }),
        intl.formatMessage({ defaultMessage: 'Keep upload modals and file views clean and focused' }),
        intl.formatMessage({ defaultMessage: 'Set a default Group for faster, more consistent uploads' }),
      ],
    },
    {
      id: 'search-document-content',
      modal: true,
      video: 'search-document-content',
      learnMore: 'https://help.stockpress.co/en/articles/15063464-search-the-text-inside-your-documents-and-pdfs',
      header: intl.formatMessage({ defaultMessage: 'Search inside your documents — not just their names' }),
      subheader: intl.formatMessage({ defaultMessage: 'Automatically extract text from PDFs and document files so every word becomes searchable across your workspace.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Extract text from PDF, PPTX, PPT, DOCX, DOC, XLSX, XLS documents, and more ' }),
        intl.formatMessage({ defaultMessage: 'Find files by the words inside them, not just filenames' }),
        intl.formatMessage({ defaultMessage: 'Search across all of your files instantly' }),
      ],
    },
    {
      id: 'search-video-content',
      modal: true,
      video: 'search-video-content',
      learnMore: 'https://help.stockpress.co/en/articles/15063519-transcribing-and-searching-your-video-and-audio-files',
      header: intl.formatMessage({ defaultMessage: 'Find any moment in your videos — just by searching' }),
      subheader: intl.formatMessage({ defaultMessage: 'Automatically extract spoken words from videos so every clip in your workspace becomes fully searchable.' }),
      others: [
        intl.formatMessage({ defaultMessage: 'Search videos by spoken words' }),
        intl.formatMessage({ defaultMessage: 'Edit extracted text to correct spelling and improve accuracy' }),
      ],
    },
  ];

  const findText = texts.find(t => t.id === feature);

  if (findText) return findText;

  return {
    header: null,
    others: [],
  };
};

export const upgradeModalIfNeeded = (feature, callback, e = null) => {
  const { dispatch } = getStore();

  if (userHasFeature(feature)) {
    callback();
  } else if (e && !e.target.classList.contains('prevent-upgrade-modal')) {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setUpgradeModal(feature));
  }
};
